import { useMemo } from "react";
import { SurgicalHistory, SurgicalHistoryControllerApi } from "@/services";
import { asPaginatedContent, getConfigObject } from "@/utils/utils";
import { usePastSurgicalHistoryCreate } from "@/queries/history/past-surgical/use-past-surgical-history-create";
import { usePastSurgicalHistoryUpdate } from "@/queries/history/past-surgical/use-past-surgical-history-update";
import { useArchivePatientSurgicalHistory } from "@/queries/history/past-surgical/use-past-surgical-history-archive";
import { HistoryRow } from "../readmission-f2f-types";
import { mapSurgicalHistoryToRow } from "../readmission-f2f-utils";
import {
  SURGICAL_HISTORY_RECORD_NAME,
  HISTORY_LIST_PAGE_SIZE,
  PATIENT_SURGICAL_HISTORY_LIST_KEY,
} from "../readmission-f2f-constant";
import {
  HistorySectionAdapter,
  UseHistorySection,
  useHistorySection,
} from "./use-history-section";

const buildPayload = (
  row: HistoryRow,
  patientId?: string,
  appointmentId?: string
): SurgicalHistory => ({
  uuid: row.uuid,
  surgeryName: row.name.trim(),
  note: row.notes.trim() || undefined,
  active: true,
  patient: { [patientId ?? ""]: "" },
  appointmentId: appointmentId ?? undefined,
});

export const useSurgicalHistorySection = (): UseHistorySection => {
  const { mutateAsync: create, isPending: isCreating } =
    usePastSurgicalHistoryCreate();
  const { mutateAsync: update, isPending: isUpdating } =
    usePastSurgicalHistoryUpdate();
  const { mutateAsync: archive, isPending: isArchiving } =
    useArchivePatientSurgicalHistory();

  const adapter: HistorySectionAdapter = useMemo(
    () => ({
      listKey: PATIENT_SURGICAL_HISTORY_LIST_KEY,
      recordName: SURGICAL_HISTORY_RECORD_NAME,
      isSaving: isCreating || isUpdating || isArchiving,
      fetchRows: ({ patientId, appointmentId, xTENANTID }) =>
        new SurgicalHistoryControllerApi(getConfigObject())
          .getPatientSurgicalHistory(
            patientId,
            appointmentId ?? undefined,
            0,
            HISTORY_LIST_PAGE_SIZE,
            undefined,
            undefined,
            true,
            false,
            xTENANTID
          )
          .then((res) =>
            (
              asPaginatedContent<SurgicalHistory>(res?.data)?.data?.content ??
              []
            ).map(mapSurgicalHistoryToRow)
          ),
      createEntry: ({ row, patientId, appointmentId, xTENANTID }) =>
        create({
          surgicalHistory: buildPayload(row, patientId, appointmentId),
          xTENANTID,
        }),
      updateEntry: ({ row, patientId, appointmentId, xTENANTID }) =>
        update({
          surgicalHistory: buildPayload(row, patientId, appointmentId),
          xTENANTID,
        }),
      archiveEntry: ({ uuid, xTENANTID }) =>
        archive({ patientSurgicalHistoryId: uuid, status: true, xTENANTID }),
    }),
    [create, update, archive, isCreating, isUpdating, isArchiving]
  );

  return useHistorySection(adapter);
};
